import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import posthog from 'posthog-js';
import PlayTriangleIcon from '../../components/PlayTriangleIcon';
import RefreshIcon from '../../components/RefreshIcon';
import { cn } from '../cn';
import { taskAdLog, taskAdLogFlushNow } from '../../utils/quest/taskAdDebugLog';


const TASK_BLOCK_ID = 'task-26003';

interface AdsgramTaskQuestProps {
  rewardAmount: number;
  secondaryRewardAmount?: number;
  onReward: () => void;
  onClaim: () => void;
  isFinished?: boolean;
  isRewardClaimed?: boolean;
  hasActiveProtection?: boolean;
  onUnavailable?: () => void;
  onAvailable?: () => void;
}

export default function AdsgramTaskQuest({
  rewardAmount,
  secondaryRewardAmount = 0,
  onReward,
  onClaim,
  isFinished = false,
  isRewardClaimed = false,
  hasActiveProtection = false,
  onUnavailable,
  onAvailable,
}: AdsgramTaskQuestProps) {
  const { t } = useTranslation();
  const taskRef = useRef<HTMLElement>(null);
  // Latest-callback refs: read only from the DOM listeners / pollers set up in the effect below
  // (all post-commit, async), never during render — the standard latest-ref idiom.
  const onRewardRef = useRef(onReward);
  // eslint-disable-next-line react-hooks/refs -- latest-callback bridge for post-commit Adsgram listeners
  onRewardRef.current = onReward;
  const onUnavailableRef = useRef(onUnavailable);
  // eslint-disable-next-line react-hooks/refs -- latest-callback bridge for post-commit Adsgram listeners
  onUnavailableRef.current = onUnavailable;
  const onAvailableRef = useRef(onAvailable);
  // eslint-disable-next-line react-hooks/refs -- latest-callback bridge for post-commit Adsgram listeners
  onAvailableRef.current = onAvailable;

  // TEMPORARY (see taskAdDebugLog.ts): log every render so the sequence of
  // prop changes around a click is visible on the Pi.
  taskAdLog('render', { isFinished, isRewardClaimed, hasActiveProtection });

  // TEMPORARY: distinguishes "the card unmounted because quest41State went
  // null (a real data-level disappearance upstream)" from "the component kept
  // rendering but a CSS 'hidden' class hid it" (the bug fixed 2026-08-26) —
  // if 'render' logs stop appearing entirely with no matching 'unmount', that
  // points at something else again (e.g. the whole modal closing/unmounting).
  useEffect(() => {
    taskAdLog('componentMounted', {});
    return () => taskAdLog('componentUnmounted', {});
  }, []);

  // Key to force-remount adsgram-task when it gets stuck in claim/done state
  const [adsgramKey, setAdsgramKey] = useState(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Guards against firing onReward twice for one tap (e.g. a fast double-tap,
  // or the host-level capture listener AND a slot's own onClick both firing
  // for the same physical tap). Reset whenever the widget gets a fresh mount.
  // Logic-only — never read during render (visuallyFinished below is the one
  // and only flag anything visual is gated on).
  const hasTriggeredRef = useRef(false);

  // Deliberate UX delay: the claim button appears (and becomes clickable) at
  // least REWARD_REVEAL_DELAY_MS after the tap, not the instant it's
  // technically unlocked. Completion itself (the cache patch + backend action
  // inside onReward) is NOT delayed — only what's shown is — so a player who
  // taps GO and stays put doesn't see the reward already grabbable and just
  // take it without ever going to do the actual task. `visuallyFinished`
  // starts equal to `isFinished` so a player returning later (or a fresh
  // reload) — where real elapsed time already covers the delay many times
  // over — finds it shown immediately, no artificial wait tacked on.
  const REWARD_REVEAL_DELAY_MS = 3500;
  const [visuallyFinished, setVisuallyFinished] = useState(isFinished);
  const revealTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Declared here (not near shouldBeGrayed/canClaim below) because the
  // widget-mount effect needs it in its own gate/deps, and effects run in
  // declaration order — this must exist before that effect is defined.
  //
  // Tied to visuallyFinished, NOT to the tap itself: a prior version hid the
  // ad widget (and showed our own button, disabled) the instant the tap fired
  // — before the reveal delay — which broke two things at once. First, the
  // CTA itself: AdsGram's own navigation (opening another Mini App or a bot
  // chat) runs asynchronously after the tap, not inside it, and the widget
  // going CSS-hidden that fast looks to whatever AdsGram checks before
  // navigating like the click never happened for real, so it silently
  // stopped firing. Second, the delay became invisible: the disabled claim
  // button showed up immediately instead of after 3.5s, which was the whole
  // point of REWARD_REVEAL_DELAY_MS in the first place. Keeping the ad widget
  // shown for the full delay window fixes both — AdsGram's own click handling
  // runs against an undisturbed, visible element, and the claim button only
  // appears once the wait is genuinely over.
  const showAdWidget = !visuallyFinished;

  useEffect(() => {
    if (!isFinished) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing visuallyFinished to the server-driven isFinished prop
      setVisuallyFinished(false);
      return;
    }
    // Already finished with no reveal timer of ours pending — either a fresh
    // mount after returning, or the server caught up on its own. Nothing to
    // delay: reflect it right away.
    if (!revealTimerRef.current) {
      setVisuallyFinished(true);
    }
  }, [isFinished]);

  // If the quest is ALREADY finished the instant this component first mounts —
  // e.g. the player did the ad, left the app, and came back to a fresh page
  // load where the server already reports it done — showAdWidget (below) is
  // false from the very first render, so the widget-mount effect never runs
  // and never gets the chance to call onAvailable(). The parent's taskAdAvailable
  // defaults to false, so without this the whole card stayed wrapped in its
  // 'hidden' class forever: rendering (confirmed in the diagnostic log), but
  // invisible. Once finished there's no ad-availability question left to
  // answer — the card must simply be shown — so this fires independently and
  // unconditionally whenever isFinished is true, delayed reveal or not.
  useEffect(() => {
    if (isFinished) {
      taskAdLog('finishedOnMount_forcingAvailable', {});
      onAvailableRef.current?.();
    }
  }, [isFinished]);

  const triggerRewardUnlock = useCallback((source: string) => {
    if (hasTriggeredRef.current) {
      taskAdLog('unlockSkipped_alreadyTriggered', { source });
      return;
    }
    hasTriggeredRef.current = true;
    posthog.capture('task_ad_reward', { block_id: TASK_BLOCK_ID });
    taskAdLog('unlockFired', { source });
    taskAdLogFlushNow();

    clearTimeout(revealTimerRef.current);
    revealTimerRef.current = setTimeout(() => {
      revealTimerRef.current = undefined;
      taskAdLog('rewardVisuallyRevealed', {});
      setVisuallyFinished(true);
    }, REWARD_REVEAL_DELAY_MS);

    onRewardRef.current();
  }, []);

  const handleRetry = useCallback(() => {
    taskAdLog('retryTapped', {});
    clearTimeout(retryTimerRef.current);
    setAdsgramKey(k => k + 1);
  }, []);

  // Listen for noads/error events from adsgram-task (availability signal), plus
  // a capture-phase click listener directly on the HOST element itself — this
  // catches a tap regardless of what AdsGram's shadow DOM does internally
  // (overlay anchors, its own click handling, etc. still bubble a click up
  // through the host in the light DOM, per how Shadow DOM event retargeting
  // works), so it does not depend on our slotted divs' own onClick actually
  // being the element the user's tap physically lands on.
  useEffect(() => {
    if (!showAdWidget) return;
    const el = taskRef.current;
    if (!el) return;

    hasTriggeredRef.current = false;
    taskAdLog('widgetMounted', { adsgramKey });

    const handleNoAds = () => {
      taskAdLog('event_noads', {});
      onUnavailableRef.current?.();
    };
    const handleError = () => {
      taskAdLog('event_error', {});
      onUnavailableRef.current?.();
    };

    // TEMPORARY: log every event the widget emits, known or not — we've
    // guessed event names wrong before (onShow/onAdStart not existing on the
    // AdsGram SDK for the interstitial). Catch-all via a fixed candidate list
    // covers the space without guessing which one actually fires here.
    const candidateEvents = ['reward', 'start', 'click', 'progress', 'view', 'impression', 'complete', 'done'];
    const genericHandlers = candidateEvents.map((name) => {
      const handler = () => taskAdLog('event_' + name, {});
      el.addEventListener(name, handler);
      return { name, handler };
    });

    const handleHostClick = (e: Event) => {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      taskAdLog('hostCaptureClick', {
        pathLength: path.length,
        pathTags: path.slice(0, 6).map((n) => (n as Element)?.tagName ?? String(n)),
      });
      triggerRewardUnlock('hostCapture');
    };
    el.addEventListener('click', handleHostClick, { capture: true });

    el.addEventListener('noads', handleNoAds);
    el.addEventListener('error', handleError);

    // TEMPORARY: observe the widget's shadow DOM for any change, to see when
    // and how it switches between its button/claim/done internal states.
    let shadowObserver: MutationObserver | null = null;
    if (el.shadowRoot && typeof MutationObserver !== 'undefined') {
      shadowObserver = new MutationObserver(() => {
        const shadow = el.shadowRoot;
        const snapshot = shadow ? shadow.innerHTML.slice(0, 300) : '';
        taskAdLog('shadowDomChanged', { snapshot });
      });
      shadowObserver.observe(el.shadowRoot, { childList: true, subtree: true, attributes: true });
    }

    // Detect availability: check shadow DOM for real content.
    // Poll every 500ms up to 6s. If content found → available. If 6s pass → unavailable.
    let resolved = false;
    const checkContent = () => {
      const shadow = el.shadowRoot;
      if (!shadow) return false;
      const inner = shadow.querySelector('a, img, [class*="task"]');
      return !!inner;
    };

    const pollInterval = setInterval(() => {
      if (resolved) return;
      if (checkContent()) {
        resolved = true;
        clearInterval(pollInterval);
        taskAdLog('availabilityResolved', { available: true });
        onAvailableRef.current?.();
      }
    }, 500);

    const fallbackTimer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      clearInterval(pollInterval);
      const available = checkContent();
      taskAdLog('availabilityResolved', { available, viaFallback: true });
      if (!available) {
        onUnavailableRef.current?.();
      } else {
        onAvailableRef.current?.();
      }
    }, 6000);

    return () => {
      el.removeEventListener('click', handleHostClick, { capture: true } as EventListenerOptions);
      el.removeEventListener('noads', handleNoAds);
      el.removeEventListener('error', handleError);
      for (const { name, handler } of genericHandlers) el.removeEventListener(name, handler);
      shadowObserver?.disconnect();
      clearInterval(pollInterval);
      clearTimeout(fallbackTimer);
    };
  }, [showAdWidget, adsgramKey, triggerRewardUnlock]);

  // Clear retry timer when quest is finished or on unmount
  useEffect(() => {
    if (isFinished) clearTimeout(retryTimerRef.current);
    return () => clearTimeout(retryTimerRef.current);
  }, [isFinished]);

  const shouldBeGrayed = isFinished && isRewardClaimed;
  // One flag, visuallyFinished, gates every visible "reward is ready" signal
  // in this component — the ad widget's visibility (via showAdWidget above),
  // the big claim button's visibility AND clickability, and the small
  // slot="claim"/"done" icon below. Nothing here reacts to the tap (or to
  // isFinished/hasTriggeredRef) any earlier than that: a previous version
  // let the small slot icon switch to a gift the instant the tap fired
  // (rewardTriggered, true immediately) while the big button waited for
  // visuallyFinished (3.5s later) — two different "reward ready" signals on
  // two different clocks, which is exactly what made the delay unreliable.
  const canClaim = visuallyFinished && !isRewardClaimed && !hasActiveProtection;

  // The ad widget (State 1) stays visible for the full REWARD_REVEAL_DELAY_MS
  // after the tap — see showAdWidget above for why hiding it any earlier
  // broke the actual CTA. AdsGram's own <adsgram-task> widget can still
  // switch itself, on its own timeline, from its 'button' slot to its
  // 'claim'/'done' slot during that window — a small round icon we style —
  // and tapping it also commits the same unlock (harmless if it already
  // fired), but its icon stays the neutral refresh glyph (not a gift) until
  // visuallyFinished, same as everything else. Our own big claim button
  // (State 2) only appears once the delay lifts and showAdWidget flips false.

  // Shared classes for the claim/done slots (іконка без тексту)
  const retryButtonClassName =
    'box-border ml-auto mr-0 flex shrink-0 grow-0 basis-auto cursor-pointer items-center justify-center gap-0 whitespace-nowrap rounded-[34px] border border-solid border-accent bg-white px-lg py-[10px] [font-family:Lato,sans-serif] text-[14px] font-bold text-accent shadow-card-raised min-h-11 min-w-11 [-webkit-tap-highlight-color:transparent]';

  // AdsGram's own widget switches itself from the "button" slot to "claim" or
  // "done" on its own timeline — we cannot hide that transition. So instead of
  // treating those slots as pure "stuck, let me retry", tapping them ALSO
  // commits the same reward-unlock as the initial GO tap (harmless if it
  // already fired), and shows a gift rather than a bare refresh icon once the
  // reward is already ours, so the widget's own visual state does not read as
  // "not done yet".
  const handleClaimOrDoneSlotClick = () => {
    taskAdLog('claimOrDoneSlotTapped', {});
    triggerRewardUnlock('claimOrDoneSlot');
    handleRetry();
  };

  return (
    <article
      className={cn(
        'flex flex-col gap-lg rounded-sm px-2xl py-lg text-white',
        shouldBeGrayed
          ? 'bg-[image:var(--gradient-disabled-progress)] opacity-60'
          : 'bg-[image:var(--gradient-brand-deep)] opacity-100',
      )}
    >
      {/* Title */}
      <p className="m-0 [font-family:'Rubik_One',sans-serif] text-[16px] leading-[24px] font-normal">
        {t("Reward for watching an ad every hour")} 🎁
      </p>

      {/* Description */}
      <p className="m-0 [font-family:Lato,sans-serif] text-[14px] leading-[16px] font-normal opacity-85">
        {t("This is an external quest that changes each time. Please read what is required below and complete this simple action to receive a great reward from us!")}
      </p>

      {/* Reward */}
      <div className="inline-flex items-center gap-xs self-start [font-family:Lato,sans-serif] text-[14px] leading-[16px] font-normal">
        <span className="font-bold text-accent">{t("Reward")}:</span>{' '}
        <span className="inline-flex flex-wrap items-center">
          <span className="inline-flex items-center">
            {rewardAmount.toLocaleString('en-US')}
            <img
              src="/ui/daily-quests/daily-quests-pearls.png"
              alt=""
              className="h-10 w-10 align-middle object-contain"
            />
          </span>
          {secondaryRewardAmount > 0 && (
            <span className="inline-flex items-center">
              <span className="mx-[2px] text-accent">+</span>
              {secondaryRewardAmount.toLocaleString('en-US')}
              <img
                src="/ui/daily-rewards/daily-rewards-exp-gift.png"
                alt=""
                className="h-10 w-10 align-middle object-contain"
              />
            </span>
          )}
        </span>
      </div>

      {/* State 1: adsgram-task with GO button — not yet tapped/finished.
          Deliberately still MOUNTED (never conditionally removed) even after
          showAdWidget flips false: AdsGram's own click handling — whatever it
          does to actually navigate to the CTA (another Mini App or a bot
          chat) — runs asynchronously after the tap, and reacting to our own
          state by unmounting the custom element mid-flight cut that off
          entirely, so the CTA silently stopped firing and the reward was
          granted with no ad ever shown. Hidden via the same CSS 'hidden'
          class the parent already uses for taskAdAvailable, never via
          removing it from the tree. */}
      <div key={adsgramKey} className={cn('flex flex-col items-center gap-xs', showAdWidget ? undefined : 'hidden')}>
          <adsgram-task
            ref={taskRef}
            data-block-id={TASK_BLOCK_ID}
            data-debug="false"
            data-debug-console="false"
            className="block w-full overflow-hidden [--adsgram-task-button-width:52px] [--adsgram-task-font-size:14px] [--adsgram-task-icon-border-radius:8px] [--adsgram-task-icon-size:40px] [--adsgram-task-icon-title-gap:12px]"
          >
            {/* slot="button" — GO */}
            <div
              slot="button"
              onClick={() => {
                taskAdLog('buttonSlotTapped', {});
                posthog.capture('task_ad_watch_clicked', { block_id: TASK_BLOCK_ID });
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = setTimeout(() => {
                  taskAdLog('retryTimerFired_forcedRemount', {});
                  setAdsgramKey(k => k + 1);
                }, 5000);

                // The CTA may open another Mini App (Telegram keeps ours in the
                // stack — fine, back returns here) or a plain bot chat, which
                // tears our whole webview down with no way back at all. AdsGram's
                // own `reward` event cannot be relied on to ever fire in that
                // second case, so the tap itself — not any signal from where it
                // leads — is what unlocks the reward and tells the backend.
                triggerRewardUnlock('buttonSlot');
              }}
              className="box-border ml-auto mr-0 flex shrink-0 grow-0 basis-auto cursor-pointer items-center justify-center gap-sm whitespace-nowrap rounded-[34px] border border-solid border-accent bg-white px-xl py-[10px] [font-family:Lato,sans-serif] text-[14px] font-bold text-accent shadow-card-raised min-h-11 min-w-11 [-webkit-tap-highlight-color:transparent]"
            >
              <PlayTriangleIcon size={10} color={'var(--color-accent)'} variant="chevron" />
              <span>GO</span>
            </div>

            {/* slot="claim" — AdsGram's own post-tap state. Also commits the unlock. */}
            <div
              slot="claim"
              onClick={handleClaimOrDoneSlotClick}
              className={retryButtonClassName}
              role="button"
              tabIndex={0}
              aria-label="Claim"
              title="Claim"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClaimOrDoneSlotClick();
                }
              }}
            >
              {visuallyFinished ? (
                <img
                  src="/ui/daily-quests/daily-quests-pearls-claim.png"
                  alt="Claim reward"
                  className="h-5 w-5 object-contain"
                />
              ) : (
                <RefreshIcon size={20} color={'var(--color-accent)'} />
              )}
            </div>

            {/* slot="done" — AdsGram's own fully-done state. Also commits the unlock. */}
            <div
              slot="done"
              onClick={handleClaimOrDoneSlotClick}
              className={retryButtonClassName}
              role="button"
              tabIndex={0}
              aria-label="Claim"
              title="Claim"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClaimOrDoneSlotClick();
                }
              }}
            >
              {visuallyFinished ? (
                <img
                  src="/ui/daily-quests/daily-quests-pearls-claim.png"
                  alt="Claim reward"
                  className="h-5 w-5 object-contain"
                />
              ) : (
                <RefreshIcon size={20} color={'var(--color-accent)'} />
              )}
            </div>
          </adsgram-task>
      </div>

      {/* State 2: the one claim button — shown the instant it's known (tap or
          already-finished mount), disabled until visuallyFinished lifts.
          Also CSS-hidden rather than conditionally rendered, for the same
          reason as State 1 above: no unmount of anything on this state flip. */}
        <button
          onClick={() => {
            taskAdLog('claimButtonTapped', { canClaim, isFinished, isRewardClaimed, hasActiveProtection });
            if (canClaim) onClaim();
          }}
          disabled={!canClaim}
          className={cn(
            'flex w-full items-center justify-center rounded-[34.925px] border border-solid border-accent bg-white px-3xl py-sm shadow-card-raised',
            canClaim ? 'cursor-pointer' : 'cursor-not-allowed',
            // Waiting out the reveal delay: same button, same icon, just
            // visibly dimmed — makes the wait itself observable instead of
            // there being nothing on screen for REWARD_REVEAL_DELAY_MS.
            !canClaim && !isRewardClaimed && !hasActiveProtection ? 'opacity-50' : undefined,
            showAdWidget ? 'hidden' : undefined,
          )}
        >
          {isRewardClaimed || hasActiveProtection ? (
            <img
              src="/ui/daily-quests/daily-quests-claimed.png"
              alt="Claimed"
              className="h-6 w-6 object-contain"
            />
          ) : (
            <img
              src="/ui/daily-quests/daily-quests-pearls-claim.png"
              alt="Claim reward"
              className="h-6 w-6 object-contain"
            />
          )}
        </button>
    </article>
  );
}
